/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState, useMemo } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import DataTable from "../../examples/Tables/DataTable";
import data from "../dashboard/components/Projects/data";
import useApi from "../../components/Utils/api";
import toast from "react-hot-toast";
import { DateRangePicker } from "rsuite";
import { afterToday } from "rsuite/cjs/DateRangePicker/disabledDateUtils";
import reportsLineChartData from "../dashboard/data/reportsLineChartData";
import {useTranslation} from "react-i18next";

function AuditTrails() {
  const {t} = useTranslation();
  const { auditTrailcolumns } = data();
  const { predefinedRanges } = reportsLineChartData;
  const [auditTrails, setAuditTrails] = useState([]);
  const [filteredAuditTrails, setFilteredAuditTrails] = useState([]);

  // Filter state
  const [filterUserId, setFilterUserId] = useState("");
  const [filterUserName, setFilterUserName] = useState("");
  const [filterModule, setFilterModule] = useState("");
  const [filterSubModule, setFilterSubModule] = useState("");
  const [filterUserLevel, setFilterUserLevel] = useState("");
  const [lastMonthDateRange, setLastMonthDateRange] = useState([
    new Date().toISOString().slice(0, 10),
    new Date().toISOString().slice(0, 10),
  ]);
  // const [fromDate, setFromDate]= useState(lastMonthDateRange[0]);
  // const [toDate, setToDate ]= useState(lastMonthDateRange[1]);
  const api = useApi();
  const formatDate = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const getAuditTrails = (lastMonthDateRange) => {
    if(lastMonthDateRange == null) return;
    setLastMonthDateRange(lastMonthDateRange);
    const data = {
      fromDate : formatDate(lastMonthDateRange[0]),
      toDate : formatDate(lastMonthDateRange[1]),
      userId : ""
    }
    api.post("audit/admin", data)
      .then((response) => {
        if(response.data.error === null) {
          // console.log(response.data)
          const sortedAuditTrails = response.data.auditTrail.sort((a, b) => {
            return new Date(b.act_time) - new Date(a.act_time);
          });
          setAuditTrails(sortedAuditTrails);
        } else {
          toast.error(response.data.error);
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          toast.error(t('no_permission_message'), { id: "PermissionError" });
          return;
        }
        console.log(error.response);
      })
  }

  useEffect(() => {
    if(lastMonthDateRange !== null ){
      getAuditTrails(lastMonthDateRange);
    }
  },[lastMonthDateRange]);

  // Derive distinct module options from all audit trails
  const moduleOptions = useMemo(() => {
    return [...new Set(auditTrails.map(a => a.module).filter(Boolean))].sort();
  }, [auditTrails]);

  // Derive distinct subModule options filtered by selected module
  const subModuleOptions = useMemo(() => {
    const source = filterModule ? auditTrails.filter(a => a.module === filterModule) : auditTrails;
    return [...new Set(source.map(a => a.subModule).filter(Boolean))].sort();
  }, [auditTrails, filterModule]);

  useEffect(() => {
    let result = [...auditTrails];
    if (filterUserId.trim()) {
      result = result.filter(a => a.userId?.toLowerCase().includes(filterUserId.trim().toLowerCase()));
    }
    if (filterUserName.trim()) {
      result = result.filter(a => a.userName?.toLowerCase().includes(filterUserName.trim().toLowerCase()));
    }
    if (filterModule) {
      result = result.filter(a => a.module === filterModule);
    }
    if (filterSubModule) {
      result = result.filter(a => a.subModule === filterSubModule);
    }
    if (filterUserLevel) {
      result = result.filter(a => a.userLevel === filterUserLevel);
    }
    setFilteredAuditTrails(result);
  }, [auditTrails, filterUserId, filterUserName, filterModule, filterSubModule, filterUserLevel]);

  const resetFilters = () => {
    setFilterUserId("");
    setFilterUserName("");
    setFilterModule("");
    setFilterSubModule("");
    setFilterUserLevel("");
  };

  return (
    <DashboardLayout>
      <MDBox
        mt={3}
        mb={3}
        sx={{
          display: "flex",
          flexDirection: "column",
          // Fills the viewport down to the screen edge: 64px navbar + 24px
          // DashboardLayout bottom padding + this box's own 24px top/bottom margins.
          height: "calc(100vh - 136px)",
        }}
      >
        <Grid container alignItems="center" sx={{ flexShrink: 0, mb: 3 }}>
          <Grid item xs={4}>
            <MDBox>
              <MDTypography variant="h5" fontWeight="medium">
                {t('audit_trails')}
              </MDTypography>
              <MDTypography variant="button" fontWeight="regular" color="secondary">
                {filteredAuditTrails.length} Records
              </MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs={4} display="flex" justifyContent="center">
            <DateRangePicker disabledDate={afterToday()} ranges={predefinedRanges} onChange={(date) => getAuditTrails(date)} format="d MMMM yyyy" value={Object.keys(lastMonthDateRange).length !== 0 ? [new Date(lastMonthDateRange[0]), new Date(lastMonthDateRange[1])] : []} />
          </Grid>
          <Grid item xs={4} />
        </Grid>
        <MDBox
          sx={(theme) => ({
            flex: "1 1 auto",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: "8px",
            background: theme.palette.white.main,
            overflow: "hidden",
          })}
        >
            {/* Filter Panel */}
            <MDBox px={2} py={1.5} sx={(theme) => ({ flexShrink: 0, borderBottom: `1px solid ${theme.palette.grey[300]}`, background: theme.palette.background.hover })}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm={6} md={3} lg={true}>
                  <TextField
                    label={t('auditTrailcolumns.userId')}
                    value={filterUserId}
                    onChange={(e) => setFilterUserId(e.target.value)}
                    fullWidth size="small" variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "38px" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={true}>
                  <TextField
                    label={t('auditTrailcolumns.userName')}
                    value={filterUserName}
                    onChange={(e) => setFilterUserName(e.target.value)}
                    fullWidth size="small" variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "38px" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={true}>
                  <TextField
                    select
                    label={t('auditTrailcolumns.module')}
                    value={filterModule}
                    onChange={(e) => { setFilterModule(e.target.value); setFilterSubModule(""); }}
                    fullWidth size="small" variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{
                      "& .MuiOutlinedInput-root": { height: "38px" },
                      "& .MuiSelect-select": { fontSize: "0.8rem" },
                    }}
                  >
                    <MenuItem value="" style={{ fontSize: "0.8rem" }}>{t('all')}</MenuItem>
                    {moduleOptions.map((mod) => (
                      <MenuItem key={mod} value={mod} style={{ fontSize: "0.8rem" }}>{mod}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={true}>
                  <TextField
                    select
                    label={t('auditTrailcolumns.subModule')}
                    value={filterSubModule}
                    onChange={(e) => setFilterSubModule(e.target.value)}
                    fullWidth size="small" variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{
                      "& .MuiOutlinedInput-root": { height: "38px" },
                      "& .MuiSelect-select": { fontSize: "0.8rem" },
                    }}
                  >
                    <MenuItem value="" style={{ fontSize: "0.8rem" }}>{t('all')}</MenuItem>
                    {subModuleOptions.map((sub) => (
                      <MenuItem key={sub} value={sub} style={{ fontSize: "0.8rem" }}>{sub}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={true}>
                  <TextField
                    select
                    label="Role"
                    value={filterUserLevel}
                    onChange={(e) => setFilterUserLevel(e.target.value)}
                    fullWidth size="small" variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{
                      "& .MuiOutlinedInput-root": { height: "38px" },
                      "& .MuiSelect-select": { fontSize: "0.8rem" },
                    }}
                  >
                    <MenuItem value="" style={{ fontSize: "0.8rem" }}>{t('all')}</MenuItem>
                    <MenuItem value="MAKER" style={{ fontSize: "0.8rem" }}>Admin</MenuItem>
                    <MenuItem value="REPORTING_ADMIN" style={{ fontSize: "0.8rem" }}>Reporting Admin</MenuItem>
                    <MenuItem value="SETTLEMENT_ADMIN" style={{ fontSize: "0.8rem" }}>Settlement Admin</MenuItem>
                    <MenuItem value="ONBOARDING_ADMIN" style={{ fontSize: "0.8rem" }}>Onboarding Admin</MenuItem>
                    <MenuItem value="ONBOARDING_REPORTING_ADMIN" style={{ fontSize: "0.8rem" }}>Onboarding &amp; Reporting Admin</MenuItem>
                    <MenuItem value="G_MAKER" style={{ fontSize: "0.8rem" }}>Guest Admin</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg="auto">
                  <MDButton variant="outlined" color="secondary" onClick={resetFilters} size="small" style={{ fontSize: "0.75rem", padding: "5px 16px", whiteSpace: "nowrap" }}>
                    {t('reset')}
                  </MDButton>
                </Grid>
              </Grid>
            </MDBox>
            <MDBox sx={{ flex: "1 1 auto", minHeight: 0 }}>
              <DataTable
                table={{ columns : auditTrailcolumns, rows : filteredAuditTrails }}
                showTotalEntries={true}
                isSorted={true}
                noEndBorder
                canSearch={false}
                entriesPerPage={true}
                entriesPerPagePosition="bottom"
                bodyMaxHeight="100%"
              />
            </MDBox>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default AuditTrails;
